import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dashboardSettingsApi, type DashboardSettingDto } from '../api/dashboardSettings.api';
import {
  DEFAULT_YEAR_SETTING_NAME,
  GROUP_COMPARISONS_OUTLIER_THRESHOLD_NAME,
} from '../dashboardSettingNames';
import { resolveDashboardSettingFromMap } from '../utils/resolveDashboardSetting';

export const dashboardSettingsKeys = {
  all: ['dashboard-settings'] as const,
  byName: (name: string) => ['dashboard-settings', 'byName', name] as const,
};

export function useDashboardSettingsQuery() {
  return useQuery<DashboardSettingDto[]>({
    queryKey: dashboardSettingsKeys.all,
    queryFn: () => dashboardSettingsApi.getAll(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useDashboardSettingsMap() {
  const query = useDashboardSettingsQuery();
  const map = useMemo(() => {
    const m = new Map<string, DashboardSettingDto>();
    for (const item of query.data ?? []) {
      m.set(item.name, item);
    }
    return m;
  }, [query.data]);

  return { ...query, map };
}

export function useDashboardSettingByName(name: string) {
  const { map, ...query } = useDashboardSettingsMap();
  return {
    ...query,
    data: map.get(name) ?? null,
  };
}

export function useDefaultYearSettingQuery() {
  return useQuery<DashboardSettingDto | null>({
    queryKey: dashboardSettingsKeys.byName(DEFAULT_YEAR_SETTING_NAME),
    queryFn: () => dashboardSettingsApi.getByName(DEFAULT_YEAR_SETTING_NAME),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Returns the persisted "Default Year" value (e.g. "2025") or '' while loading.
 * Use this in pages to pre-select the year dropdown.
 */
export function useDefaultYear(): string {
  const { data } = useDefaultYearSettingQuery();
  return data?.value ?? '';
}

export function useUpdateDashboardSettingMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { id: string; name: string; value: string }) =>
      dashboardSettingsApi.update(params.id, { name: params.name, value: params.value }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dashboardSettingsKeys.all });
      queryClient.invalidateQueries({ queryKey: ['dashboard-settings', 'byName'] });
    },
  });
}

export {
  parseDecimalSetting,
  parseIntegerSetting,
  parsePercentSetting,
  parseUsdSetting,
} from '../utils/dashboardSettingParsers';

/** @deprecated Prefer useGroupComparisonsOutlierThresholdResolved().value */
export function useGroupComparisonsOutlierThreshold(): number {
  const { map } = useDashboardSettingsMap();
  return useMemo(
    () => resolveDashboardSettingFromMap(map, GROUP_COMPARISONS_OUTLIER_THRESHOLD_NAME).value,
    [map]
  );
}

export function useParsedDashboardSetting(name: string): number {
  const { map } = useDashboardSettingsMap();
  return useMemo(() => resolveDashboardSettingFromMap(map, name).value, [map, name]);
}
